import { onMounted, onUnmounted, nextTick } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useDebounceFn } from '@vueuse/core';

const settings = {
  headerClass: '.site-header',
  resizeDebounceTime: 250,
};

// Get the vertical center of the header in pixels
function getHeaderVerticalCenterPx(headerEl: HTMLElement): number {
  const rect = headerEl.getBoundingClientRect();
  return rect.height / 2;
}

export const useHeaderIntersection = (
  trigger: Ref<HTMLElement | null> | ComputedRef<HTMLElement | null>,
  options: {
    debug?: boolean;
    elementStart?: string;
    elementEnd?: string;
  } = {},
) => {
  // Global state for the header intersection
  const headerIsIntersecting = useState('headerIsIntersecting');
  let scrollTrigger: ScrollTrigger | null = null;

  const {
    debug = false,
    elementStart = 'top',
    elementEnd = 'bottom',
  } = options;

  const updateScrollTrigger = () => {
    const el = trigger.value;
    if (el && el instanceof HTMLElement) {
      const header = document.querySelector(
        settings.headerClass,
      ) as HTMLElement | null;
      if (!header) return;

      const triggerOffset = getHeaderVerticalCenterPx(header);

      if (!scrollTrigger) {
        scrollTrigger = ScrollTrigger.create({
          trigger: el,
          start: `${elementStart} ${triggerOffset}px`,
          end: `${elementEnd} ${triggerOffset}px`,
          markers: debug,
          onEnter: () => (headerIsIntersecting.value = true),
          onEnterBack: () => (headerIsIntersecting.value = true),
          onLeave: () => (headerIsIntersecting.value = false),
          onLeaveBack: () => (headerIsIntersecting.value = false),
        });
      } else {
        scrollTrigger.vars.start = `${elementStart} ${triggerOffset}px`;
        scrollTrigger.vars.end = `${elementEnd} ${triggerOffset}px`;
        scrollTrigger.refresh();
      }
    }
  };

  // Create debounced version of updateScrollTrigger with 250ms delay
  const debouncedUpdateScrollTrigger = useDebounceFn(
    updateScrollTrigger,
    settings.resizeDebounceTime,
  );

  onMounted(() => {
    nextTick(() => {
      updateScrollTrigger();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', debouncedUpdateScrollTrigger);
    }
  });

  onUnmounted(() => {
    if (scrollTrigger) {
      scrollTrigger.kill();
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', debouncedUpdateScrollTrigger);
    }
  });
};
